import { ChangeDetectorRef, Component, DestroyRef, HostBinding, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivationEnd } from '@angular/router';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { CountItem } from '@frontend/sports/common/core/data-access/sport-model';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { ScrollContainerService } from '@frontend/sports/common/core/utils/scroll-container';
import { Tournament } from '@frontend/sports/types/components/tournaments';
import produce from 'immer';
import { clone, flatten, sumBy } from 'lodash-es';
import { firstValueFrom } from 'rxjs';

import { CompetitionTreeComponent } from '../../competition-list/competition-tree.component';
import { BaseTreeItem, NavEventModel, TreeItem, TreeItemType } from '../../event-details-common/event-details-common.models';
import { getLeagueId } from '../../event-details-common/event-details-common.utils';
import { TreeComponent } from '../../tree/tree.component';
import { LiveEventsNavigationSubscriptionService } from '../live-events-navigation/live-events-navigation-subscription.service';
import { NavigationTrackingService } from '../navigation-tracking/navigation-tracking.service';
import { LeagueParameters, PreMatchEventsNavigationService } from './prematch-events-navigation.service';

@Component({
    selector: 'ms-prematch-events-navigation',
    templateUrl: 'prematch-events-navigation.component.html',
    providers: [LiveEventsNavigationSubscriptionService, NavigationTrackingService],
    imports: [CompetitionTreeComponent, TreeComponent],
})
export class PrematchEventsNavigationComponent implements OnInit, OnDestroy {
    @Input() tree: BaseTreeItem;
    @Input() selectedEvent: EventModel;
    @HostBinding('class') className = 'event-details-left-menu prematch-nav';

    sport?: CountItem;
    tournaments?: Tournament[];
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private navService: PreMatchEventsNavigationService,
        private routerHelper: RouterEventsService,
        private subscriptionsService: LiveEventsNavigationSubscriptionService,
        private scrollContainerService: ScrollContainerService,
        private trackingService: NavigationTrackingService,
        private changeRef: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.initNavModel();
        this.routerHelper.currentActivationEnd.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => this.activationEnd(data));

        this.navService
            .getObservableTree()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((tree) => this.updateTree(tree));

        this.subscriptionsService.updatedEvents$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => this.updateEvents(event));
    }

    async initNavModel(): Promise<void> {
        this.tree = produce(this.tree, (draft: BaseTreeItem) => {
            const leagueParameters: LeagueParameters = {
                leagueIds: [],
                virtualLeagueIds: [],
            };

            draft.children[0].children
                .filter((l) => l.expanded)
                .forEach((l) => {
                    l.loading = true;
                    if (l.isVirtual) {
                        leagueParameters.virtualLeagueIds.push(l.id);
                    } else {
                        leagueParameters.leagueIds.push(l.id);
                    }
                });

            if (leagueParameters.leagueIds.length !== 0 || leagueParameters.virtualLeagueIds.length !== 0) {
                this.getEvents(leagueParameters).then(() => this.scrollContainerService.scrollTo(undefined, 0));
            }
        });

        const sportTree = await firstValueFrom(this.navService.getSportTree(this.selectedEvent.sport.id));
        if (sportTree) {
            this.sport = sportTree.sport;
            this.tournaments = sportTree.tournaments;
        }
    }

    toggleItem(item: TreeItem): void {
        if (item.type === TreeItemType.League) {
            this.navService.toggleLeague(item.id, !!item.isVirtual);
            this.toggleLeague(item);
        } else if (item.type === TreeItemType.Region && item.collapsible) {
            this.navService.toggleRegion();
            this.toggleRegion();
        }
    }

    select(event: EventModel): void {
        this.tree = produce(this.tree, (draft: BaseTreeItem) => {
            const region = draft.children[0];

            this.setSelection(region, this.selectedEvent, false);
            this.setSelection(region, event, true);

            this.selectedEvent = event;
            this.navService.isEventSelectedFromPrematch = true;
        });
    }

    onToggleMarkets(): void {
        this.navService.toggleMarkets();
        this.tree = produce(this.tree, (draft: BaseTreeItem) => {
            const region = draft.children[0];
            this.trackingService.trackMarketsToggle(this.navService.state.showMarkets, region.leftNavType);
            region.toggleMarkets = this.navService.state.showMarkets;
            this.getNavEvents(region, true).forEach((e) => (e.showMarkets = this.navService.state.showMarkets));
        });
    }

    private toggleRegion(): void {
        this.tree = produce(this.tree, (draft: BaseTreeItem) => {
            const region = draft.children[0];
            region.expanded = !region.expanded;
            this.trackingService.trackRegionToggle(region);

            const expandedLeagues = region.children.filter((l) => l.expanded);
            if (expandedLeagues.length > 0) {
                this.modifyLeaguesState(expandedLeagues, region.expanded);
            }
        });
    }

    private toggleLeague(item: TreeItem): void {
        this.tree = produce(this.tree, (draft: BaseTreeItem) => {
            const league = draft.children[0].children.find((l) => l.id === item.id)!;
            league.expanded = !league.expanded;
            this.trackingService.trackLeagueToggle(league, item.leftNavType);
            this.modifyLeaguesState([league], league.expanded);
        });
    }

    private modifyLeaguesState(leagues: TreeItem[], eventsExpanded: boolean): void {
        if (eventsExpanded) {
            leagues.forEach((l) => (l.loading = true));
            const leagueParameters: LeagueParameters = {
                leagueIds: leagues.filter((l) => !l.isVirtual).map((l) => l.id),
                virtualLeagueIds: leagues.filter((l) => l.isVirtual).map((l) => l.id),
            };

            this.getEvents(leagueParameters);
        } else {
            const subscribedEvents: EventModel[] = [];
            leagues.forEach((league) => {
                subscribedEvents.push(...league.events.map((e) => e.data));
                league.events = [];
            });
            this.subscriptionsService.unsubscribe(subscribedEvents);
        }
    }

    private async getEvents(leagueParameters: LeagueParameters): Promise<void> {
        //TODO R: looks like a memory leak
        this.navService
            .getEventsForLeagues(leagueParameters)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((groupedEvents) => {
                this.tree = produce(this.tree, (draft: BaseTreeItem) => {
                    const region = draft.children[0];
                    region.children
                        .filter((league) => !!groupedEvents[league.id])
                        .forEach((league) => {
                            const mappedEvents = this.navService.mapNavEvents(groupedEvents[league.id]);
                            league.events = mappedEvents;
                            league.count = mappedEvents.length;
                            league.loading = false;
                        });

                    this.setSelection(region, this.selectedEvent, true);
                    region.count = sumBy(region.children, (l) => l.count!);
                });

                const all = leagueParameters.leagueIds.length + leagueParameters.virtualLeagueIds.length !== 1;
                const leagueId = leagueParameters.leagueIds[0] || leagueParameters.virtualLeagueIds[0];
                const navEvents = this.getNavEvents(this.tree.children[0], all, leagueId).map((e) => e.data);
                this.changeRef.markForCheck();
                this.subscriptionsService.subscribe(navEvents);
            });
    }

    private setSelection(region: TreeItem, event: EventModel, selected: boolean): void {
        const league = region.children.find((l) => l.id === getLeagueId(event));
        if (!league) {
            return;
        }

        league.selected = selected;

        const currentEvent = league.events.find((e) => e.data.id === event.id);
        if (!currentEvent) {
            return;
        }

        currentEvent.selected = selected;
    }

    private activationEnd(data?: ActivationEnd): void {
        if (data) {
            const event = <EventModel>data.snapshot.data['model'].model;
            if (event && this.selectedEvent.id !== event.id) {
                this.select(event);
                this.expandLeague(event);
            }
        }
        this.changeRef.markForCheck();
    }

    private expandLeague(event: EventModel): void {
        const league = this.tree.children[0].children.find((l) => l.id === getLeagueId(event));
        if (league && !league.expanded) {
            this.toggleLeague(league);
        }
    }

    private updateTree(updatedTree: BaseTreeItem): void {
        const region = updatedTree.children[0];
        const subscribedEvents: EventModel[] = [];
        if (region.expanded && region.children.some((l) => l.expanded)) {
            const navEvents = this.getNavEvents(region, true);
            subscribedEvents.push(...navEvents.map((e) => e.data));
        }

        this.setSelection(region, this.selectedEvent, true);
        this.subscriptionsService.refreshSubscriptions(subscribedEvents);
        this.tree = updatedTree;
        this.changeRef.markForCheck();
    }

    private updateEvents(events: EventModel[]): void {
        this.tree = produce(this.tree, (draft: BaseTreeItem) => {
            events.forEach((event) => {
                const league = draft.children[0].children.find((l) => l.id === getLeagueId(event));
                if (!league) {
                    return;
                }

                const oldEventIndex = league.events.findIndex((e) => e.data.id === event.id);
                if (oldEventIndex !== -1) {
                    league.events[oldEventIndex] = {
                        data: clone(event),
                        selected: event.id === this.selectedEvent.id,
                        showMarkets: this.navService.state.showMarkets,
                    };
                }
            });
        });
        this.changeRef.markForCheck();
    }

    private getNavEvents(region: TreeItem, all: boolean, leagueId?: number): NavEventModel[] {
        const navEvents = region.children.filter((l) => (all ? l.expanded : l.id === leagueId)).map((l) => l.events);

        return flatten(navEvents);
    }

    ngOnDestroy(): void {
        this.navService.isEventSelectedFromPrematch = false;
    }
}
